/**
 * Satisfaction Survey Page — v8-7-3
 * Standalone full-screen route at /branch/survey/:token — no auth required.
 * 4-question NPS-style survey sent 24h after meeting end.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

interface SurveyContext {
  is_expired: boolean;
  is_submitted: boolean;
  room_name: string;
  appointment_at: string | null;
}

interface SurveyForm {
  satisfaction_score: number;
  relevance_score: number;
  style_score: number;
  style_label: string;
  free_text: string;
}

const STYLE_OPTIONS = [
  { value: 'too_technical', label: 'Too Technical' },
  { value: 'just_right', label: 'Just Right' },
  { value: 'too_simple', label: 'Too Simple' },
];

function NpsRow({ label, min, max, value, onChange }: {
  label: string; min: number; max: number; value: number; onChange: (v: number) => void;
}) {
  return (
    <div>
      <p className="text-sm text-gray-300 mb-2">{label}</p>
      <div className="flex gap-2 flex-wrap">
        {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`w-10 h-10 rounded-lg text-sm font-semibold border transition-colors ${
              value === n
                ? 'bg-amber-500 border-amber-500 text-black'
                : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-amber-500'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function ThankYouScreen() {
  return (
    <div className="fixed inset-0 bg-gray-950 flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="text-5xl">✅</div>
        <h1 className="text-2xl font-bold text-white">Thank you for your feedback.</h1>
        <p className="text-gray-400">It helps us improve our service.</p>
      </div>
    </div>
  );
}

function ExpiredScreen() {
  return (
    <div className="fixed inset-0 bg-gray-950 flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="text-5xl">⏳</div>
        <h1 className="text-2xl font-bold text-white">This survey has expired.</h1>
        <p className="text-gray-400">Survey links are valid for 7 days after your meeting.</p>
      </div>
    </div>
  );
}

export default function SurveyPage() {
  const { token } = useParams<{ token: string }>();
  const [context, setContext] = useState<SurveyContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<SurveyForm>({
    satisfaction_score: 0,
    relevance_score: 0,
    style_score: 3,
    style_label: 'just_right',
    free_text: '',
  });

  useEffect(() => {
    if (!token) return;
    fetch(`/api/v1/bookings/survey/${token}`)
      .then((r) => r.json())
      .then((data) => setContext(data))
      .catch(() => setContext({ is_expired: true, is_submitted: false, room_name: '', appointment_at: null }))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (context?.is_expired) return <ExpiredScreen />;
  if (submitted || context?.is_submitted) return <ThankYouScreen />;

  const canSubmit = form.satisfaction_score > 0 && form.relevance_score > 0;

  const handleSubmit = async () => {
    if (!canSubmit || !token) return;
    setSubmitting(true);
    try {
      await fetch(`/api/v1/bookings/survey/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const apptLabel = context?.appointment_at
    ? new Date(context.appointment_at).toLocaleString('en-ZA', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <div className="fixed inset-0 bg-gray-950 overflow-y-auto p-4 md:p-8 flex items-start justify-center">
      <div className="max-w-lg w-full bg-gray-900 rounded-2xl p-8 space-y-6 my-8">
        <div>
          <h1 className="text-amber-400 text-xl font-bold">Meeting Feedback</h1>
          {context?.room_name && (
            <p className="text-gray-400 text-sm mt-1">
              {context.room_name}{apptLabel ? ` — ${apptLabel}` : ''}
            </p>
          )}
        </div>

        {/* Q1: Overall satisfaction (1-10) */}
        <NpsRow
          label="1. Overall meeting satisfaction (1 = poor, 10 = excellent)"
          min={1}
          max={10}
          value={form.satisfaction_score}
          onChange={(v) => setForm((f) => ({ ...f, satisfaction_score: v }))}
        />

        {/* Q2: Relevance (1-5) */}
        <NpsRow
          label="2. Was the advice relevant to your situation? (1 = not at all, 5 = very relevant)"
          min={1}
          max={5}
          value={form.relevance_score}
          onChange={(v) => setForm((f) => ({ ...f, relevance_score: v }))}
        />

        {/* Q3: Communication style */}
        <div>
          <p className="text-sm text-gray-300 mb-2">3. Communication style rating</p>
          <div className="flex gap-2 flex-wrap">
            {STYLE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm((f) => ({ ...f, style_label: opt.value, style_score: STYLE_OPTIONS.indexOf(opt) + 1 }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  form.style_label === opt.value
                    ? 'bg-amber-500 border-amber-500 text-black'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-amber-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Q4: Free text (optional) */}
        <div>
          <p className="text-sm text-gray-300 mb-2">4. Any additional feedback? (optional)</p>
          <textarea
            placeholder="Share your thoughts..."
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg p-3 text-sm resize-none h-20 focus:outline-none focus:border-amber-500"
            value={form.free_text}
            onChange={(e) => setForm((f) => ({ ...f, free_text: e.target.value }))}
          />
        </div>

        <button
          type="button"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
          className="w-full py-3 bg-amber-500 text-black font-bold rounded-lg disabled:opacity-40 hover:bg-amber-400 transition-colors"
        >
          {submitting ? 'Submitting…' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
}
